import { useRecentlyChangedRowsApi } from "@/contexts/RecentlyChangedRowsContext";
import type { ModalActions } from "@/hooks/useModal";
import type { ProjectSummary } from "@/hooks/useProjectBoardData";
import { assignmentAuditLabel, projectAuditLabel } from "@/utils/auditLabels";
import { clearAssignmentEmployees } from "@/utils/assignmentEmployees";
import { formatEmployeeDisplayName } from "@/utils/nameUtils";
import { useMemo, useState } from "react";

interface ConfirmClearAllAssignmentsModalProps extends ModalActions {
  project: ProjectSummary;
  onConfirm?: () => void;
  refreshSilently: () => Promise<void>;
  updateProjectOptimistically: (
    projectId: string,
    updates: Partial<ProjectSummary>,
  ) => void;
  formatRole: (id: string | null | undefined) => string;
}

export function ConfirmClearAllAssignmentsModal({
  project,
  onConfirm,
  refreshSilently,
  updateProjectOptimistically,
  formatRole,
  close,
}: ConfirmClearAllAssignmentsModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { markSelfEdit } = useRecentlyChangedRowsApi();

  // The confirmation counts every role on the project, matching the visibility
  // rule. The write below only touches the roles that actually have assignees —
  // clearAssignmentEmployees no-ops on an empty one, audit entry included.
  const roleCount = project.project_assignments.length;
  const targets = useMemo(
    () => project.project_assignments.filter((a) => a.employees.length > 0),
    [project.project_assignments],
  );

  const handleConfirm = async () => {
    if (submitting) return;

    setError(null);
    setSubmitting(true);

    const targetIds = new Set(targets.map((assignment) => assignment.id));
    // Optimistic wipe covers only the roles we are about to write. Clearing
    // is_confirmed on an already-empty role would claim a change the server
    // never makes, which the next realtime sync would silently revert.
    updateProjectOptimistically(project.id, {
      project_assignments: project.project_assignments.map((assignment) =>
        targetIds.has(assignment.id)
          ? { ...assignment, employees: [], is_confirmed: false }
          : assignment,
      ),
    });

    try {
      // clearAssignmentEmployees swallows its own errors and always resolves,
      // so failures are collected through onError rather than by catching.
      const failures: string[] = [];
      await Promise.all(
        targets.map((assignment) => {
          const nameById = new Map<string, string>();
          for (const employee of assignment.employees) {
            const displayName = formatEmployeeDisplayName(employee);
            if (displayName) nameById.set(employee.id, displayName);
          }
          // clearAssignmentEmployees marks the self-edit before it writes.
          return clearAssignmentEmployees(
            assignment,
            markSelfEdit,
            () => {},
            (message) => {
              failures.push(message);
            },
            {
              recordLabel: assignmentAuditLabel(
                projectAuditLabel(project),
                assignment,
                formatRole,
              ),
              nameById,
              officeId: project.office_id ?? null,
            },
          );
        }),
      );

      if (failures.length > 0) {
        // One refresh for the whole batch, not one per failed row.
        await refreshSilently();
        const cleared = targets.length - failures.length;
        setError(
          `Cleared ${cleared} of ${targets.length} roles. ${failures[0]}`,
        );
        setSubmitting(false);
        return;
      }

      onConfirm?.();
      close();
    } catch (err) {
      await refreshSilently();
      setError(
        err instanceof Error ? err.message : "Failed to clear assignments",
      );
      setSubmitting(false);
    }
  };

  return (
    <div>
      {error && (
        <div className="error-banner">
          <span className="error-banner__message">{error}</span>
        </div>
      )}
      <p>
        Remove every assignee from all {roleCount} roles on "{project.name}"?
        The roles themselves will stay.
      </p>
      <div className="modal-actions">
        <button
          type="button"
          className="secondary-button"
          onClick={close}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="danger-button"
          data-testid="confirm-clear-all-assignments"
          onClick={handleConfirm}
          disabled={submitting}
        >
          {submitting ? "Clearing..." : "Clear All Assignees"}
        </button>
      </div>
    </div>
  );
}
